import type {
  SupportV2WipeProgress,
  SupportV2WipeRunProgress,
  SupportV2WipeStepProgress,
} from "@redotech/admin-sdk/rpc/schema/support/get-support-v2-wipe-progress";
import {
  RedoBadge,
  type RedoBadgeProps,
} from "@redotech/arbiter/badge/redo-badge";
import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Card } from "@redotech/arbiter/card";
import { Divider } from "@redotech/arbiter/divider/divider";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { RedoProgressBar } from "@redotech/arbiter/progress-bar/redo-progress-bar";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import RefreshIcon from "@redotech/redo-icons/featured-refresh-cw-3.svg";
import StopIcon from "@redotech/redo-icons/stop-circle.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useMemo, useState, type JSXElementConstructor } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

const WIPE_CONFIRM_LITERAL = "WIPE";
const PROGRESS_POLL_INTERVAL_MS = 3000;
const numberFormatter = new Intl.NumberFormat();

type WipeRunStatus = SupportV2WipeRunProgress["status"];
type WipeStepStatus = SupportV2WipeStepProgress["status"];

const isActiveWipeRunStatus = (status: WipeRunStatus | undefined) =>
  status === "pending" || status === "running";

const isTerminalWipeStepStatus = (status: WipeStepStatus) =>
  status === "completed" || status === "failed" || status === "skipped";

const RUN_STATUS_BADGE: Record<
  WipeRunStatus,
  {
    color: RedoBadgeProps["color"];
    Icon?: JSXElementConstructor<unknown>;
    text: string;
  }
> = {
  cancelled: { color: "gray", Icon: StopIcon, text: "Cancelled" },
  completed: { color: "success", Icon: CheckIcon, text: "Completed" },
  failed: { color: "error", Icon: AlertTriangleIcon, text: "Failed" },
  pending: { color: "gray", Icon: ClockIcon, text: "Pending" },
  running: { color: "blue", Icon: ClockIcon, text: "Running" },
};

const STEP_STATUS_BADGE: Record<
  WipeStepStatus,
  { color: RedoBadgeProps["color"]; text: string }
> = {
  completed: { color: "success", text: "Done" },
  failed: { color: "error", text: "Failed" },
  pending: { color: "gray", text: "Pending" },
  running: { color: "blue", text: "Deleting" },
  skipped: { color: "gray", text: "Skipped" },
};

const formatCount = (value: number) => numberFormatter.format(value);

const formatProgressDate = (value: string | null) =>
  value ? new Date(value).toLocaleString() : "None";

const formatDuration = (start: string | null, end: string | null) => {
  if (!start) {
    return "Not started";
  }
  const startTime = new Date(start).getTime();
  const endTime = end ? new Date(end).getTime() : Date.now();
  const seconds = Math.max(0, Math.round((endTime - startTime) / 1000));
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ${seconds % 60}s`;
  }
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const formatError = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
};

const getOverallPercentage = (progress: SupportV2WipeProgress | undefined) => {
  const run = progress?.run;
  if (!run) {
    return 0;
  }
  if (run.status === "completed") {
    return 100;
  }
  // Deletes have no known denominator, so progress is measured by how many of
  // the fixed wipe steps have reached a terminal state.
  const steps = progress.steps;
  if (steps.length === 0) {
    return 0;
  }
  const done = steps.filter((step) =>
    isTerminalWipeStepStatus(step.status),
  ).length;
  return Math.max(0, Math.min(100, Math.round((done / steps.length) * 100)));
};

const RunStatusBadge = ({ status }: { status: WipeRunStatus }) => {
  const config = RUN_STATUS_BADGE[status];
  return (
    <RedoBadge
      color={config.color}
      segmentLeading={
        config.Icon
          ? { type: "icon", Icon: config.Icon, size: "sm" }
          : undefined
      }
      size="sm"
      text={config.text}
    />
  );
};

const WipeStepRow = ({ step }: { step: SupportV2WipeStepProgress }) => {
  const config = STEP_STATUS_BADGE[step.status];
  return (
    <Flex align="center" gap="md" justify="space-between" w="full">
      <Flex align="center" gap="sm" grow={1}>
        <Text fontSize="sm">{step.label}</Text>
        <RedoBadge color={config.color} size="xs" text={config.text} />
      </Flex>
      <Text fontSize="sm" textColor="secondary">
        {formatCount(step.deletedCount)} deleted
      </Text>
    </Flex>
  );
};

export const WipeSupportV2Card = memo(function WipeSupportV2Card({
  teamId,
}: {
  teamId: string;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [remigrateModalOpen, setRemigrateModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [workflowId, setWorkflowId] = useState<string | undefined>();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const progressQueryKey = useMemo(
    () => ["supportV2WipeProgress", teamId] as const,
    [teamId],
  );

  const progressQuery = useQuery({
    enabled: !!teamId,
    queryFn: () => rpcClient.getSupportV2WipeProgress({ teamId }),
    queryKey: progressQueryKey,
    refetchInterval: (query) =>
      isActiveWipeRunStatus(query.state.data?.run?.status)
        ? PROGRESS_POLL_INTERVAL_MS
        : false,
    refetchOnWindowFocus: false,
  });

  const progress = progressQuery.data;
  const run = progress?.run;
  const isLive = isActiveWipeRunStatus(run?.status);
  const overallPercentage = getOverallPercentage(progress);

  const wipeMutation = useMutation({
    mutationFn: () => rpcClient.wipeTeamSupportV2({ teamId }),
    onSuccess: (result) => {
      setWorkflowId(result.workflowId);
      setModalOpen(false);
      setConfirmText("");
      void progressQuery.refetch();
    },
  });

  const wipeAndRemigrateMutation = useMutation({
    mutationFn: () => rpcClient.wipeAndRemigrateTeamSupportV2({ teamId }),
    onSuccess: (result) => {
      setWorkflowId(result.workflowId);
      setRemigrateModalOpen(false);
      setConfirmText("");
      void progressQuery.refetch();
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () =>
      rpcClient.cancelSupportV2Wipe({
        teamId,
        ...(run?.workflowId ? { workflowId: run.workflowId } : {}),
      }),
    onSuccess: () => {
      setCancelModalOpen(false);
      void queryClient.invalidateQueries({ queryKey: progressQueryKey });
    },
  });

  const confirmEnabled =
    confirmText.trim() === teamId ||
    confirmText.trim() === WIPE_CONFIRM_LITERAL;

  const openModal = useHandler(() => {
    wipeMutation.reset();
    setConfirmText("");
    setModalOpen(true);
  });

  const closeModal = useHandler(() => {
    if (!wipeMutation.isPending) {
      setModalOpen(false);
      setConfirmText("");
    }
  });

  const openRemigrateModal = useHandler(() => {
    wipeAndRemigrateMutation.reset();
    setConfirmText("");
    setRemigrateModalOpen(true);
  });

  const closeRemigrateModal = useHandler(() => {
    if (!wipeAndRemigrateMutation.isPending) {
      setRemigrateModalOpen(false);
      setConfirmText("");
    }
  });

  const openCancelModal = useHandler(() => {
    cancelMutation.reset();
    setCancelModalOpen(true);
  });

  const closeCancelModal = useHandler(() => {
    if (!cancelMutation.isPending) {
      setCancelModalOpen(false);
    }
  });

  return (
    <>
      <Card borderRadius="md" title="Wipe team support-v2 data">
        <Flex dir="column" gap="lg" w="full">
          <Flex align="flex-start" gap="md" justify="space-between" wrap="wrap">
            <Text fontSize="sm" textColor="secondary">
              Full reset of this team&apos;s migrated support-v2 (shopper-ai)
              data so it can be cleanly re-migrated. This permanently deletes
              topics, tickets, messages, integrations, tags, macros, views,
              folders, the ticket-number counter, and migration history. The job
              is blocked if the team has real Commerce Agent conversation data.
              Use{" "}
              <Text as="span" fontWeight="medium">
                Wipe &amp; re-migrate
              </Text>{" "}
              to reset and immediately re-run a full migration.
            </Text>
            <Flex align="center" gap="xs">
              <RedoBadge
                color={isLive ? "blue" : "gray"}
                size="sm"
                text={isLive ? "Live polling" : "Manual refresh"}
              />
              {run && <RunStatusBadge status={run.status} />}
            </Flex>
          </Flex>

          {progressQuery.isLoading && (
            <Text fontSize="sm" textColor="secondary">
              Loading wipe progress...
            </Text>
          )}
          {progressQuery.error && (
            <Text fontSize="sm" textColor="error">
              Progress error: {formatError(progressQuery.error)}
            </Text>
          )}

          {run && progress && (
            <Flex dir="column" gap="md">
              <Flex align="center" gap="md" justify="space-between" wrap="wrap">
                <Text fontSize="xs" textColor="secondary">
                  Workflow {run.workflowId}
                </Text>
                {isLive && (
                  <RedoButton
                    hierarchy="secondary"
                    IconLeading={StopIcon}
                    onClick={openCancelModal}
                    pending={cancelMutation.isPending}
                    size="sm"
                    text="Cancel wipe"
                    theme="destructive"
                  />
                )}
              </Flex>

              <Flex dir="column" gap="xs">
                <Flex align="center" justify="space-between">
                  <Text fontSize="sm" fontWeight="medium">
                    {formatCount(run.totalDeleted)} rows deleted
                  </Text>
                  <Text fontSize="sm" textColor="secondary">
                    {overallPercentage}%
                  </Text>
                </Flex>
                <RedoProgressBar
                  labelStyle="none"
                  percentage={overallPercentage}
                  style="thin"
                />
                <Text fontSize="xs" textColor="secondary">
                  Duration: {formatDuration(run.startedAt, run.completedAt)}
                </Text>
              </Flex>

              {run.lastError && (
                <Text fontSize="sm" textColor="error">
                  {run.lastError}
                </Text>
              )}

              <Flex dir="column" gap="xs">
                {progress.steps.map((step) => (
                  <WipeStepRow key={step.id} step={step} />
                ))}
              </Flex>
            </Flex>
          )}

          <Divider />

          <Flex align="center" gap="md" justify="space-between" wrap="wrap">
            <Flex align="center" gap="sm" wrap="wrap">
              <RedoButton
                hierarchy="primary"
                IconLeading={TrashIcon}
                onClick={openModal}
                text="Wipe support-v2 data"
                theme="destructive"
              />
              <RedoButton
                hierarchy="secondary"
                IconLeading={RefreshIcon}
                onClick={openRemigrateModal}
                text="Wipe & re-migrate"
                theme="destructive"
              />
            </Flex>
            <Flex align="center" gap="md" wrap="wrap">
              {workflowId && (
                <Text fontSize="sm" fontWeight="medium">
                  Started: {workflowId}
                </Text>
              )}
              {wipeMutation.error && (
                <Text fontSize="sm" fontWeight="medium" textColor="error">
                  Error: {formatError(wipeMutation.error)}
                </Text>
              )}
              {wipeAndRemigrateMutation.error && (
                <Text fontSize="sm" fontWeight="medium" textColor="error">
                  Error: {formatError(wipeAndRemigrateMutation.error)}
                </Text>
              )}
              <RedoButton
                hierarchy="secondary"
                IconLeading={RefreshIcon}
                onClick={() => void progressQuery.refetch()}
                pending={progressQuery.isFetching}
                size="sm"
                text="Refresh"
              />
            </Flex>
          </Flex>

          {progress && progress.history.length > 1 && (
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                Recent runs
              </Text>
              {progress.history.map((historyRun) => (
                <Flex
                  align="center"
                  gap="md"
                  justify="space-between"
                  key={historyRun.id}
                  w="full"
                >
                  <RunStatusBadge status={historyRun.status} />
                  <Text fontSize="xs" textColor="secondary">
                    {formatProgressDate(historyRun.createdAt)} ·{" "}
                    {formatCount(historyRun.totalDeleted)} deleted
                  </Text>
                </Flex>
              ))}
            </Flex>
          )}
        </Flex>
      </Card>

      {modalOpen && (
        <RedoModal
          headerIconAlignment="inline"
          modalSize="sm"
          onModalCloseRequested={closeModal}
          primaryButton={{
            disabled: !confirmEnabled,
            IconLeading: TrashIcon,
            onClick: async () => {
              await wipeMutation.mutateAsync();
            },
            pending: wipeMutation.isPending,
            text: "Wipe data",
            theme: "destructive",
          }}
          secondaryButton={{
            disabled: wipeMutation.isPending,
            onClick: closeModal,
            text: "Cancel",
          }}
          subtitle="This permanently deletes the team's migrated support-v2 data and migration history. It will fail fast (and delete nothing) if the team has real Commerce Agent data."
          theme="error"
          title="Wipe team support-v2 data?"
          TitleIcon={TrashIcon}
        >
          <Flex dir="column" gap="sm">
            <Text fontSize="sm" textColor="secondary">
              Type the team id{" "}
              <Text as="span" fontFamily="monospace">
                {teamId}
              </Text>{" "}
              or{" "}
              <Text as="span" fontFamily="monospace">
                {WIPE_CONFIRM_LITERAL}
              </Text>{" "}
              to confirm.
            </Text>
            <RedoTextInput
              autoCompleteDisabled
              label="Confirmation"
              placeholder={WIPE_CONFIRM_LITERAL}
              setValue={setConfirmText}
              size="sm"
              value={confirmText}
            />
            {wipeMutation.error && (
              <Text fontSize="sm" textColor="error">
                {formatError(wipeMutation.error)}
              </Text>
            )}
          </Flex>
        </RedoModal>
      )}

      {remigrateModalOpen && (
        <RedoModal
          headerIconAlignment="inline"
          modalSize="sm"
          onModalCloseRequested={closeRemigrateModal}
          primaryButton={{
            disabled: !confirmEnabled,
            IconLeading: RefreshIcon,
            onClick: async () => {
              await wipeAndRemigrateMutation.mutateAsync();
            },
            pending: wipeAndRemigrateMutation.isPending,
            text: "Wipe & re-migrate",
            theme: "destructive",
          }}
          secondaryButton={{
            disabled: wipeAndRemigrateMutation.isPending,
            onClick: closeRemigrateModal,
            text: "Cancel",
          }}
          subtitle="This permanently deletes the team's migrated support-v2 data, then immediately runs a fresh full migration. The wipe is blocked (and nothing is deleted or re-migrated) if the team has real Commerce Agent data. Watch the wipe card for the reset and the migrate card for the re-migration."
          theme="error"
          title="Wipe and re-migrate team?"
          TitleIcon={RefreshIcon}
        >
          <Flex dir="column" gap="sm">
            <Text fontSize="sm" textColor="secondary">
              Type the team id{" "}
              <Text as="span" fontFamily="monospace">
                {teamId}
              </Text>{" "}
              or{" "}
              <Text as="span" fontFamily="monospace">
                {WIPE_CONFIRM_LITERAL}
              </Text>{" "}
              to confirm.
            </Text>
            <RedoTextInput
              autoCompleteDisabled
              label="Confirmation"
              placeholder={WIPE_CONFIRM_LITERAL}
              setValue={setConfirmText}
              size="sm"
              value={confirmText}
            />
            {wipeAndRemigrateMutation.error && (
              <Text fontSize="sm" textColor="error">
                {formatError(wipeAndRemigrateMutation.error)}
              </Text>
            )}
          </Flex>
        </RedoModal>
      )}

      {cancelModalOpen && (
        <RedoModal
          headerIconAlignment="inline"
          modalSize="sm"
          onModalCloseRequested={closeCancelModal}
          primaryButton={{
            IconLeading: StopIcon,
            onClick: async () => {
              await cancelMutation.mutateAsync();
            },
            pending: cancelMutation.isPending,
            text: "Cancel wipe run",
            theme: "destructive",
          }}
          secondaryButton={{
            disabled: cancelMutation.isPending,
            onClick: closeCancelModal,
            text: "Keep running",
          }}
          subtitle="This requests Temporal cancellation and marks the durable wipe run as cancelled so this card stops treating it as active. Already-deleted data is not restored."
          theme="error"
          title="Cancel wipe run?"
          TitleIcon={StopIcon}
        >
          <Flex dir="column" gap="sm">
            {run && (
              <Text
                fontFamily="monospace"
                fontSize="xs"
                overflow="hidden"
                textColor="secondary"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {run.workflowId}
              </Text>
            )}
            {cancelMutation.error && (
              <Text fontSize="sm" textColor="error">
                {formatError(cancelMutation.error)}
              </Text>
            )}
          </Flex>
        </RedoModal>
      )}
    </>
  );
});
